/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import Popup from '../../components/popups/popup';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { formatPrice } from '../../utils';
import UserDropdown from '../../components/dropdowns/userDropdown';
import TimeDropdown from '../../components/dropdowns/timeDropdown';
import PagePaymentService from '../../services/pagePaymentService';
import PageDropdown from '../../components/dropdowns/pageDropdown';
import DeletePagePaymentModal from '../../components/modals/pagePayment/deletePagePaymentModal';
import ViewPagePaymentModal from '../../components/modals/pagePayment/viewPagePaymentModal';
import PdfPagePaymentList from '../../components/prints/pdfs/pagePayment/pdfPagePaymentList';
import PdfOnePagePayment from '../../components/prints/pdfs/pagePayment/pdfOnePagePayment';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import { AuthContext } from '../../provider/authProvider';

const PagePayments = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [pagePayments, setPagePayments] = useState([]);
    const [selectedPagePayment, setSelectedPagePayment] = useState({});
    const [totalPrice, setTotalPrice] = useState(0);

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (pagePayment) => {
        setSelectedPagePayment(pagePayment);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (pagePayment) => {
        setSelectedPagePayment(pagePayment);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSelectedUser(null);
            setSelectedPage(null);
            await fetchPagePayments();
        }
    };

    const fetchPagePayments = async (page = 1, branchId = null, pageId = null, userId = null) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                branchId, pageId, userId,
                ...sortState,
                ...selectedTime
            };
            const data = await PagePaymentService.fetch(page, filters);
            const { payments, pagePaymentsAmount } = data;

            setTotalPrice(pagePaymentsAmount);

            if (payments.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setPagePayments([]);
                }
            } else {
                setPagePayments((prevs) => (page === 1 ? payments : [...prevs, ...payments]));
            }
        } catch (error) {
            console.error('Error fetching page payments:', error);
        } finally {
            setIsLoading(false);
        }
    };


    const resetFilter = () => {
        setSelectedUser(null);
        setSelectedPage(null);
        setHasMore(true)
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
        if (page !== 1) setPage(1);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            fetchPagePayments(1, selectedBranch?.id, selectedPage?.id, selectedUser?.id);
            return;
        }
    }, []);

    useEffect(() => {
        if (page > 1) {
            fetchPagePayments(page, selectedBranch?.id, selectedPage?.id, selectedUser?.id);
        }
    }, [page]);

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchPagePayments(1, selectedBranch?.id, selectedPage?.id, selectedUser?.id);
    }, [selectedBranch, selectedUser, selectedPage, sortState, selectedTime]);


    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchPagePayments(page + 1, selectedBranch?.id, selectedPage?.id, selectedUser?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeletePagePaymentModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} pagePayment={selectedPagePayment} />
                <ViewPagePaymentModal open={openView} handleClose={handleCloseView} pagePaymentId={selectedPagePayment.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Totals row */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'end'
            }}>
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        height: 60,
                        width: isTablet ? '90%' : '30%'
                    }}
                >
                    <Box width={isTablet ? '80%' : '75%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 14 : 16 }}>
                            {t('totalPriceAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPrice)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: isTablet ? '20%' : '25%'
                        }}>
                        <PdfPagePaymentList
                            data={{
                                branch: selectedBranch,
                                user: selectedUser,
                                page: selectedPage,
                                time: selectedTime,
                            }} />
                    </Box>
                </Box>
            </Box>


            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        mb: 2,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,

                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation='vertical' />
                        <Box sx={{ alignContent: 'center', height: 50, width: '95%' }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                    </Box>
                    {allBranchPermission &&
                        <Box>
                            <Divider />
                            <Box sx={{
                                alignContent: 'center',
                                height: 50,
                                width: '100%'
                            }}>
                                <BranchDropdown
                                    isFilter={true}
                                    selectedBranch={selectedBranch}
                                    setSelectedBranch={setSelectedBranch}
                                />
                            </Box>
                        </Box>
                    }


                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <PageDropdown
                            isFilter={true}
                            selectedPage={selectedPage}
                            setSelectedPage={setSelectedPage}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <UserDropdown
                            isFilter={true}
                            selectedUser={selectedUser}
                            setSelectedUser={setSelectedUser}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>



                </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'40%'}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        {allBranchPermission && <Divider orientation="vertical" />}
                        {allBranchPermission && <Box width={'18%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>}
                        <Divider orientation="vertical" />
                        <Box width={allBranchPermission ? '18%' : '20%'}>
                            <PageDropdown
                                isFilter={true}
                                selectedPage={selectedPage}
                                setSelectedPage={setSelectedPage}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={allBranchPermission ? '18%' : '20%'}>
                            <UserDropdown
                                isFilter={true}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                            />
                        </Box>
                        <Divider orientation="vertical" />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 226px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>
                                {t('number')}
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('page')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('page')}
                                    {sortState.sortBy === 'page' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('user')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('user')}
                                    {sortState.sortBy === 'user' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('branch')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('branch')}
                                    {sortState.sortBy === 'branch' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('priceAmount')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('priceAmount')}
                                    {sortState.sortBy === 'priceAmount' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('createdAt')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('createdAt')}
                                    {sortState.sortBy === 'createdAt' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagePayments.map((payment, index) => (
                            <TableRow
                                key={payment.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{index + 1}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.page.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.user.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.branch.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(payment.priceAmount)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.createdAt}</TableCell>

                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(payment)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }} onClick={() => handleOpenDelete(payment)}>
                                            <DeleteOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.red.bg }}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }}>
                                            <PdfOnePagePayment
                                                id={payment.id}
                                            />
                                        </Box>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PagePayments;
